"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useReducedMotion } from "motion/react";
import { useCallback } from "react";
import { useDetectTouchDevice } from "../../hooks/index.js";
import { cn } from "../../utils/cn.js";
import { Image } from "../Image/index.js";
import { carouselVariants } from "./Carousel.variants.js";
import { CarouselControls } from "./CarouselControls.js";
import { HeroCarouselView } from "./hero/HeroCarouselView.js";
import { HeroSlide } from "./hero/HeroSlide.js";
import { useCarouselEngine } from "./useCarouselEngine.js";
function getImageSlideProps(isSelectedSlide) {
    return {
        inertProps: isSelectedSlide ? {} : {
            inert: 'true'
        },
        tabIndexProps: isSelectedSlide ? {
            tabIndex: 0
        } : {}
    };
}
function renderCarouselImage({ imageSrc, imageClassName, alt, objectFit = 'cover', lazyLoad = false, lazyRootMargin = '200px', lazyViewportRef, lazyForceLoad = false }) {
    return /*#__PURE__*/ jsx(Image, {
        src: imageSrc,
        className: imageClassName,
        alt: alt,
        objectFit: objectFit,
        width: "full",
        height: "full",
        objectPosition: "center",
        lazyLoad: lazyLoad,
        lazyRootMargin: lazyRootMargin,
        lazyViewportRef: lazyViewportRef,
        lazyForceLoad: lazyForceLoad
    });
}
function renderSlide({ slide, index, slideClasses, isImageCarousel, isSelectedSlide, minSlideWidth, slideWidth, lazyLoad, lazyLoadRootMargin, viewportRef }) {
    if (isImageCarousel) {
        const imageSlide = slide;
        const alt = imageSlide.title ?? `Slide ${index + 1}`;
        const ariaLabel = imageSlide.title ?? `Slide ${index + 1}`;
        const { inertProps, tabIndexProps } = getImageSlideProps(isSelectedSlide);
        return /*#__PURE__*/ jsx("li", {
            className: slideClasses,
            "aria-hidden": !isSelectedSlide,
            ...inertProps,
            ...tabIndexProps,
            "aria-label": ariaLabel,
            children: renderCarouselImage({
                imageSrc: imageSlide.image,
                imageClassName: imageSlide.imageClassName,
                alt,
                objectFit: 'cover',
                lazyLoad,
                lazyRootMargin: lazyLoadRootMargin,
                lazyViewportRef: viewportRef,
                lazyForceLoad: isSelectedSlide
            })
        }, slide.id);
    }
    const contentSlide = slide;
    const customMinWidthStyle = 'min' === slideWidth && minSlideWidth ? {
        minWidth: minSlideWidth
    } : {};
    return /*#__PURE__*/ jsx("li", {
        className: slideClasses,
        style: customMinWidthStyle,
        "aria-label": contentSlide.title ?? `Slide ${index + 1}`,
        children: contentSlide.childrenNodes
    }, slide.id);
}
function getDefaultedCarouselOptions(props, isTouchDevice) {
    const isImageCarousel = 'image' === props.type;
    return {
        isImageCarousel,
        loop: isImageCarousel ? props.loop ?? true : false,
        dragFree: isImageCarousel ? false : props.dragFree ?? false,
        hideArrowsOnMobileOrTouch: isTouchDevice ? 'always' : 'responsive',
        slidesToScrollProp: isImageCarousel ? void 0 : props.slidesToScroll,
        isLightBoxSupported: isImageCarousel && (props.isLightBoxSupported ?? false),
        startIndex: void 0 !== props.startIndex && props.startIndex >= 0 ? props.startIndex : 0,
        minSlideWidth: 'minSlideWidth' in props ? props.minSlideWidth : void 0,
        slideWidth: props.slideWidth ?? ('content' === props.type ? 'auto' : 'image')
    };
}
function StandardCarousel(props) {
    const { slotClassNames = {}, slides, ariaLabel, ...restProps } = props;
    const lazyLoad = 'lazyLoad' in props ? props.lazyLoad ?? false : false;
    const lazyLoadRootMargin = 'lazyLoadRootMargin' in props ? props.lazyLoadRootMargin ?? '200px' : '200px';
    const isTouchDevice = useDetectTouchDevice();
    const prefersReducedMotion = useReducedMotion() ?? false;
    const { pagination, onSlideChange } = props;
    const { isImageCarousel, loop, dragFree, hideArrowsOnMobileOrTouch, slidesToScrollProp, isLightBoxSupported, startIndex, minSlideWidth, slideWidth } = getDefaultedCarouselOptions(props, isTouchDevice);
    const showArrowBtns = 'numbers' === pagination;
    const showDotBtns = 'dots' === pagination;
    const showSlideNum = isImageCarousel && 'numbers' === pagination;
    const { emblaCallbackRef, viewportRef, selectedIndex, onDotButtonClick, prevBtnDisabled, nextBtnDisabled, prevBtnHidden, onPrevButtonClick, onNextButtonClick, autoplayIsPlaying, toggleAutoplay, resetAutoplayOnInteraction, handleMouseEnter, handleMouseLeave, autoplayInViewRef, hasAutoplay } = useCarouselEngine({
        loop,
        startIndex,
        dragFree,
        slidesToScroll: slidesToScrollProp,
        plugins: isImageCarousel ? props.plugins : void 0,
        carouselIdProp: props.carouselId,
        onSlideChange,
        prefersReducedMotion
    });
    const showBlurBox = isImageCarousel && (showSlideNum || isLightBoxSupported || hasAutoplay);
    const onLightboxButtonClick = (callback)=>callback();
    const { wrapperParentClass = '', wrapperClass = '', rootClass = '', viewportClass = '', containerClass = '', slideClass = '', buttonClass = '', dotsPaginationClass = '', dotButtonClass = '', dotClass = '', blurBoxClass = '', blurBoxDividerClass = '', blurBoxNumbersClass = '', blurBoxLightboxButtonClass = '', blurBoxAutoplayButtonClass = '' } = slotClassNames;
    const { wrapperParentSlot, wrapperSlot, rootSlot, viewportSlot, containerSlot, prevButtonSlot, nextButtonSlot, hiddenButtonSlot, dotsPaginationSlot, dotButtonSlot, slideSlot } = carouselVariants({
        ...restProps,
        type: props.type,
        loop,
        hideArrowsOnMobileOrTouch: hideArrowsOnMobileOrTouch,
        bleedSpacing: restProps.bleedSpacing
    });
    const wrapperParentClasses = cn(wrapperParentSlot(), wrapperParentClass);
    const wrapperClasses = cn(wrapperSlot(), wrapperClass);
    const rootClasses = cn(rootSlot(), rootClass);
    const viewportClasses = cn(viewportSlot(), viewportClass);
    const containerClasses = cn(containerSlot(), containerClass);
    const slideClasses = cn(slideSlot(), slideClass);
    const currentSlide = selectedIndex + 1;
    const totalSlides = slides?.length ?? 0;
    const onPrevNextButtonClick = useCallback((callback)=>resetAutoplayOnInteraction(callback), [
        resetAutoplayOnInteraction
    ]);
    const arrowButtonType = isImageCarousel ? 'image' : 'content';
    const controlsConfig = {
        showArrowBtns,
        showDotBtns,
        showBlurBox,
        showSlideNum,
        isLightBoxSupported,
        hasAutoplay,
        isImageCarousel,
        arrowButtonType
    };
    return /*#__PURE__*/ jsx("div", {
        "data-testid": "carousel-wrapper-parent",
        className: wrapperParentClasses,
        children: /*#__PURE__*/ jsx("div", {
            "data-testid": "carousel-wrapper",
            className: wrapperClasses,
            children: /*#__PURE__*/ jsxs("section", {
                "aria-label": ariaLabel,
                "data-testid": "carousel-root",
                className: rootClasses,
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
                ref: autoplayInViewRef,
                children: [
                    /*#__PURE__*/ jsx("div", {
                        "data-testid": "carousel-viewport",
                        className: viewportClasses,
                        ref: emblaCallbackRef,
                        children: /*#__PURE__*/ jsx("ul", {
                            "data-testid": "carousel-container",
                            className: containerClasses,
                            children: slides?.map((slide, index)=>{
                                const isSelectedSlide = index === selectedIndex;
                                return renderSlide({
                                    slide,
                                    index,
                                    slideClasses,
                                    isImageCarousel,
                                    isSelectedSlide,
                                    minSlideWidth,
                                    slideWidth,
                                    lazyLoad,
                                    lazyLoadRootMargin,
                                    viewportRef
                                });
                            })
                        })
                    }),
                    /*#__PURE__*/ jsx(CarouselControls, {
                        config: controlsConfig,
                        slides: slides,
                        selectedIndex: selectedIndex,
                        prevBtnDisabled: prevBtnDisabled,
                        nextBtnDisabled: nextBtnDisabled,
                        prevBtnHidden: prevBtnHidden,
                        onPrevButtonClick: onPrevButtonClick,
                        onNextButtonClick: onNextButtonClick,
                        onDotButtonClick: onDotButtonClick,
                        onPrevNextButtonClick: onPrevNextButtonClick,
                        resetAutoplayOnInteraction: resetAutoplayOnInteraction,
                        onLightboxButtonClick: onLightboxButtonClick,
                        toggleAutoplay: toggleAutoplay,
                        autoplayIsPlaying: autoplayIsPlaying,
                        slotClassNames: {
                            buttonClass,
                            dotsPaginationClass,
                            dotButtonClass,
                            dotClass,
                            blurBoxClass,
                            blurBoxDividerClass,
                            blurBoxNumbersClass,
                            blurBoxLightboxButtonClass,
                            blurBoxAutoplayButtonClass
                        },
                        variantSlots: {
                            prevButtonSlot,
                            nextButtonSlot,
                            hiddenButtonSlot,
                            dotsPaginationSlot,
                            dotButtonSlot
                        },
                        currentSlide: currentSlide,
                        totalSlides: totalSlides
                    })
                ]
            })
        })
    });
}
function CarouselRoot(props) {
    if ('hero' === props.type) return /*#__PURE__*/ jsx(HeroCarouselView, {
        ...props
    });
    return /*#__PURE__*/ jsx(StandardCarousel, {
        ...props
    });
}
const Carousel = Object.assign(CarouselRoot, {
    HeroSlide: HeroSlide
});
var Carousel_Carousel = Carousel;
export default Carousel_Carousel;
export { Carousel, getImageSlideProps };
